import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Mail, XCircle } from "lucide-react";
import { useCart } from "../context/CartContext";
import { getCheckoutStatus } from "../lib/api";

const MAX_POLL_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 2000;

export default function OrderConfirmationPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(sessionId ? "polling" : "missing");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const { clear } = useCart();
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) return;

    let attempts = 0;
    let cancelled = false;
    let timer;

    const poll = async () => {
      attempts += 1;
      try {
        const data = await getCheckoutStatus(sessionId);
        if (cancelled) return;

        if (data.payment_status === "paid") {
          setOrder(data);
          setStatus("paid");
          if (!clearedRef.current) {
            clearedRef.current = true;
            clear();
          }
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts >= MAX_POLL_ATTEMPTS) {
          setStatus("timeout");
          setOrder(data);
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || "Unable to fetch order status");
        setStatus("error");
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, clear]);

  if (status === "missing") {
    return (
      <StateShell>
        <div className="flex items-center gap-3 mb-4">
          <XCircle size={28} strokeWidth={1.5} />
          <div className="label-xs">No order found</div>
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-medium mb-4">
          We couldn't find your order
        </h1>
        <p className="text-neutral-600 mb-8">
          This page is only reachable after completing checkout.
        </p>
        <Link
          to="/shop"
          className="inline-flex items-center px-8 py-4 bg-black text-white text-xs uppercase tracking-[0.25em]"
        >
          Continue shopping
        </Link>
      </StateShell>
    );
  }

  if (status === "polling") {
    return (
      <StateShell>
        <div className="flex items-center gap-3 mb-4 text-neutral-500">
          <Loader2 size={24} className="animate-spin" strokeWidth={1.5} />
          <div className="label-xs">Confirming your payment</div>
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-medium mb-4">
          Just a moment…
        </h1>
        <p className="text-neutral-600">
          We're finalizing your order with Stripe. This usually takes a few
          seconds.
        </p>
      </StateShell>
    );
  }

  if (status === "error" || status === "expired" || status === "timeout") {
    return (
      <StateShell>
        <div className="flex items-center gap-3 mb-4">
          <XCircle size={28} strokeWidth={1.5} />
          <div className="label-xs">
            {status === "expired" ? "Session expired" : "Something went wrong"}
          </div>
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-medium mb-4">
          We couldn't confirm your payment
        </h1>
        <p className="text-neutral-600 mb-8">
          {error ||
            "If you completed payment, check your email for a Stripe receipt. Otherwise, please try again."}
        </p>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/cart"
            className="inline-flex items-center px-8 py-4 bg-black text-white text-xs uppercase tracking-[0.25em]"
          >
            Return to cart
          </Link>
          <a
            href="mailto:support@simplestylemode.com"
            className="inline-flex items-center gap-2 px-8 py-4 border border-black text-xs uppercase tracking-[0.25em]"
          >
            <Mail size={14} /> Contact support
          </a>
        </div>
      </StateShell>
    );
  }

  // status === "paid"
  const amountLabel = order?.amount_total
    ? `$${(order.amount_total / 100).toFixed(2)}`
    : "";
  const ship = order?.shipping;

  return (
    <div
      data-testid="order-confirmation"
      className="max-w-3xl mx-auto px-6 py-20 md:py-28"
    >
      <div className="flex items-center gap-3 mb-6">
        <CheckCircle2 size={28} strokeWidth={1.5} />
        <div className="label-xs">Order confirmed</div>
      </div>
      <h1 className="font-display text-5xl md:text-6xl font-medium mb-4">
        Thank you{order?.name ? `, ${order.name.split(" ")[0]}` : ""}.
      </h1>
      <p className="text-neutral-600 leading-relaxed mb-10 max-w-xl">
        Your payment of <strong>{amountLabel}</strong> was received
        {order?.email && (
          <>
            {" "}
            and a receipt has been sent to <strong>{order.email}</strong>
          </>
        )}
        . You'll get another email with tracking details once your order ships.
      </p>

      <div className="border border-[color:var(--ssm-border)] p-6 md:p-8 space-y-6">
        <div>
          <div className="label-xs mb-3">Items</div>
          <div className="divide-y divide-[color:var(--ssm-border)]">
            {(order?.items || []).map((i, idx) => (
              <div key={idx} className="py-3 flex justify-between text-sm gap-4">
                <div className="flex-1">
                  <div className="font-medium">{i.name}</div>
                  <div className="text-neutral-500 text-xs">Qty {i.quantity}</div>
                </div>
                <div>${((i.amount_total || 0) / 100).toFixed(2)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
          {ship && (
            <div>
              <div className="label-xs mb-2">Shipping to</div>
              <div className="text-neutral-700 leading-relaxed">
                {order?.name && (
                  <>
                    {order.name}
                    <br />
                  </>
                )}
                {ship.line1}
                {ship.line2 ? `, ${ship.line2}` : ""}
                <br />
                {ship.city}, {ship.state} {ship.postal_code}
                <br />
                {ship.country}
              </div>
            </div>
          )}
          <div>
            <div className="label-xs mb-2">Summary</div>
            <div className="space-y-1 text-neutral-700">
              <div className="flex justify-between font-medium">
                <span>Total paid</span>
                <span>{amountLabel}</span>
              </div>
              <div className="text-xs text-neutral-500">
                Session: {order?.session_id?.slice(-10)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap gap-3">
        <Link
          to="/shop"
          data-testid="order-continue-link"
          className="inline-flex items-center px-8 py-4 bg-black text-white text-xs uppercase tracking-[0.25em]"
        >
          Continue shopping
        </Link>
        <a
          href="mailto:support@simplestylemode.com"
          className="inline-flex items-center gap-2 px-8 py-4 border border-black text-xs uppercase tracking-[0.25em]"
        >
          <Mail size={14} /> Contact support
        </a>
      </div>
    </div>
  );
}

const StateShell = ({ children }) => (
  <div className="max-w-2xl mx-auto px-6 py-24 md:py-32">{children}</div>
);
